import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { FileText, Download, Mail, Calendar } from 'lucide-react';

export default function StudentReportCardArchive({ studentId }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCards = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/parent/report-cards/${studentId}`);
      setCards(res.data || []);
    } catch { /* silent for the section being optional */ }
    finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const downloadPdf = async (cardId, studentName, month) => {
    try {
      const res = await api.get(`/teacher/report-card/${cardId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ReportCard_${(studentName || 'Student').replace(/\s+/g, '_')}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download PDF'); }
  };

  if (!studentId) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="student-report-card-archive">
      <div className="px-4 py-3 bg-gradient-to-br from-purple-600 to-indigo-700 text-white flex items-center gap-2">
        <FileText className="w-4 h-4" />
        <h3 className="font-bold text-sm">Monthly Report Cards</h3>
      </div>
      <div className="p-3 space-y-2">
        {loading && <div className="text-xs text-slate-400 text-center py-4">Loading…</div>}
        {!loading && cards.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-6">No report cards yet. Your teacher will publish them at the end of each month.</div>
        )}
        {cards.map(c => (
          <div key={c.id} className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition" data-testid={`student-rc-${c.id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-purple-700" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">{c.month_label}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <span>{c.classes_completed_text || c.month_classes_count || 0} classes</span>
                  {c.final_grade && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">Grade {c.final_grade}</span>}
                  {c.finalized && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-bold">FINALIZED</span>}
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost" className="text-purple-600 h-8" onClick={() => downloadPdf(c.id, c.student_name, c.month)} data-testid={`student-rc-pdf-${c.id}`}>
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
